import { tt } from '@agos/shared'

/**
 * The three sections §9.324 added to the VAT screen - the register's fallback pair, each channel's
 * slot, and the rates nothing recognises.
 *
 * **The owner:** *"we have DPH in Predaj a účtovanie - Sadzby DPH, but also in pokladna -
 * nastavenia. Can you remove one and merge them together?… Ii hate it like we have it now."*
 *
 * `GET /vat/configuration` (BACKEND.md §10A.113) answers all four stores in one read. Only the
 * reading and the rules are here; what can be wrong is the *meaning* of a null slot, not the markup.
 */

export interface VatSlotChannel {
  id: number
  name: string
  slug: string
  onPremise: boolean
  /** `null` means nobody has chosen - the channel follows `onPremise`. Not the same as a choice. */
  vatSlot: 'dine_in' | 'delivery' | null
  /** What would actually be charged, resolved by the server so one rule decides it. */
  effectiveSlot: 'dine_in' | 'delivery'
  isActive: boolean
}

export interface UnknownRateProduct {
  id: number
  name: string
  active: boolean
  vatRateDineIn: number | null
  vatRateDelivery: number | null
}

export interface RegisterRates {
  dineIn: string | null
  delivery: string | null
  fallback: string | null
}

export interface VatConfiguration {
  register: RegisterRates
  channels: VatSlotChannel[]
  unknownRateProducts: UnknownRateProduct[]
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
}

function integer(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

export function vatConfigurationOf(payload: unknown): VatConfiguration {
  const outer = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<
    string,
    unknown
  >
  const register = (
    typeof outer.register === 'object' && outer.register !== null ? outer.register : {}
  ) as Record<string, unknown>

  return {
    register: {
      dineIn: text(register.dineIn),
      delivery: text(register.delivery),
      fallback: text(register.fallback),
    },
    channels: (Array.isArray(outer.channels) ? outer.channels : []).flatMap((entry) => {
      if (typeof entry !== 'object' || entry === null) return []
      const row = entry as Record<string, unknown>
      const stored = row.vatSlot
      const effective = row.effectiveSlot
      return [
        {
          id: integer(row.id) ?? 0,
          name: text(row.name) ?? '',
          slug: text(row.slug) ?? '',
          onPremise: row.onPremise === true,
          vatSlot: stored === 'dine_in' || stored === 'delivery' ? stored : null,
          // The server resolves this; a client that recomputed it would be a second copy of the
          // fallback rule, which is what §10A.113 put in one place.
          effectiveSlot: effective === 'dine_in' || effective === 'delivery' ? effective : 'delivery',
          isActive: row.isActive === true,
        },
      ]
    }),
    unknownRateProducts: (Array.isArray(outer.unknownRateProducts)
      ? outer.unknownRateProducts
      : []
    ).flatMap((entry) => {
      if (typeof entry !== 'object' || entry === null) return []
      const row = entry as Record<string, unknown>
      return [
        {
          id: integer(row.id) ?? 0,
          name: text(row.name) ?? '',
          active: row.active === true,
          vatRateDineIn: integer(row.vatRateDineIn),
          vatRateDelivery: integer(row.vatRateDelivery),
        },
      ]
    }),
  }
}

/** What the two slots are called where a person reads them. */
export function slotLabel(slot: 'dine_in' | 'delivery'): string {
  return slot === 'dine_in' ? tt('Na mieste') : tt('Rozvoz a odber')
}

/**
 * What a channel's row says about where its slot comes from.
 *
 * **The distinction this exists for**: a null slot is *following the channel's «na mieste» flag*,
 * not *somebody chose this*. A screen that showed only the resolved value would make a setting
 * nobody made look deliberate - and the owner asked for this control precisely because the flag and
 * the tax slot are different questions.
 */
export function slotSource(channel: VatSlotChannel): string {
  if (channel.vatSlot !== null) {
    return tt('Nastavené ručne')
  }
  return `${tt('Podľa prepínača «Na mieste»')}: ${slotLabel(channel.effectiveSlot)}`
}

/** Whether clearing is offered - only where something was set, so the button never lies. */
export function mayClearSlot(channel: VatSlotChannel): boolean {
  return channel.vatSlot !== null
}

/**
 * The body `PATCH /sales-channels/{id}` takes for a slot change.
 *
 * `null` clears it back to *follow `on_premise`*, which is a different thing from picking the other
 * value and is why the screen offers it as its own action.
 */
export function slotPayload(slot: 'dine_in' | 'delivery' | null): Record<string, unknown> {
  return { vatSlot: slot }
}

/**
 * How a product's impossible rate is described.
 *
 * Both rates are named even when only one is wrong, because the pair is what somebody has to look at
 * to decide - and the decision is the owner's.
 */
export function unknownRateNote(product: UnknownRateProduct): string {
  const dineIn = product.vatRateDineIn === null ? '-' : `${product.vatRateDineIn} %`
  const delivery = product.vatRateDelivery === null ? '-' : `${product.vatRateDelivery} %`
  return `${tt('Na mieste')} ${dineIn} · ${tt('Rozvoz')} ${delivery}`
}

/**
 * Whether the register has a fallback pair at all.
 *
 * Empty is a real state - the columns are text and an unconfigured franchise holds `''` - and it
 * matters because a product with no rate then falls through to the code's own 5 and 19.
 */
export function registerIsConfigured(register: RegisterRates): boolean {
  return register.dineIn !== null && register.delivery !== null
}

export function registerNote(register: RegisterRates): string {
  if (registerIsConfigured(register)) {
    return tt('Použije sa pri produkte, ktorý nemá vlastnú sadzbu.')
  }
  return tt('Nie je nastavená. Produkt bez vlastnej sadzby dostane 5 % na mieste a 19 % na rozvoz.')
}
